import {
  AiResponseThoughtStep,
  ThoughtStepKey,
} from "@/v2/entities/chain-of-thoughts/ChainOfThoughts.model";

export type GenericJson = Record<string, unknown>;

export interface ToolsStepData {
  stepKey: "tools";
  allowedTools: string[];
  unavailable?: Array<{
    toolName: string;
    missingKeys: string[];
  }>;
}

export interface ContextStepData {
  stepKey: "context";
  allowedSources: string[];
  userQuery: string;
  contextData: {
    knowledgeRawContext?: unknown[];
    ticketsRawContext?: unknown[];
    wikiKnowledgeContext?: unknown[];
    perplexityResponse?: unknown;
  };
}

export interface RerankStepData {
  stepKey: "rerank";
  rerankResults: {
    context: unknown[];
    sources: {
      kb: unknown[];
      tickets: unknown[];
      wiki: unknown[];
    };
    contextExport: unknown[];
    isKnowledgeExist: boolean;
  };
}

export interface StrictStepData {
  stepKey: "strict";
  isStrictMode: boolean;
  isAllowed: boolean;
}

export interface PlanStepData {
  stepKey: "plan";
  planContent: string;
}

export interface DetectLanguageStepData {
  stepKey: "detect-language";
  detectedLanguage: string;
}

export interface ThinkingStepData {
  stepKey: "thinking";
  isThinkingNeeded: boolean;
  reason: string;
}

export interface PrereqsStepData {
  stepKey: "prereqs";
  prerequisites: {
    isAllPrerequisitesMet: boolean;
    prerequisitesProvided: string[];
    prerequisitesNotProvided: string[];
    reason: string;
  };
}

export interface ImagesStepData {
  stepKey: "images";
  processedImages: {
    images: Array<{
      image: string;
      description: string;
    }>;
  };
}

export interface ToolInvocationStepData {
  stepKey: "tool-invocation";
  toolName: string;
  toolArgs: Record<string, unknown>;
  toolId: string;
}

export interface ToolResponseStepData {
  stepKey: "tool-response";
  toolName: string;
  toolId: string;
  result: unknown;
  state: "result" | "error" | "pending";
}

export interface AdditionalContextStepData {
  stepKey: "additional-context";
  additionalContext: Record<string, any>;
}

export interface TopicStepData {
  stepKey: "topic";
  topic: string;
}

export type ChainOfThoughtStep =
  | ToolsStepData
  | ContextStepData
  | RerankStepData
  | StrictStepData
  | PlanStepData
  | DetectLanguageStepData
  | ThinkingStepData
  | PrereqsStepData
  | ImagesStepData
  | ToolInvocationStepData
  | ToolResponseStepData
  | AdditionalContextStepData
  | TopicStepData;

export type StepKey = ChainOfThoughtStep["stepKey"];

export type PolicyContent = {
  brand?: string;
  strictMode?: boolean;
  brief?: string;
  allowabilityResult?: {
    isAllowed: boolean;
    reason?: string;
    notAllowedReason?: string;
    allowedReason?: string;
  };
};

export type RerankContent = {
  isKnowledgeExist?: boolean;
  sources?: {
    kb?: Array<{ id?: string } & GenericJson>;
    wiki?: Array<{ id?: string } & GenericJson>;
    tickets?: string[];
  };
};

export type GenerateContent = {
  text?: string;
  usage?: {
    promptTokens?: number;
    completionTokens?: number;
    totalTokens?: number;
  };
  toolResults?: unknown[];
};

export type PromptContent = {
  system?: string;
};

export type PayloadContent = {
  sourceLanguage?: string;
  isAllowed?: boolean;
  isStrictMode?: boolean;
  _raw?: GenericJson;
};

export type StrictContent = {
  isAllowed: boolean;
  reason?: string;
  notAllowedReason?: string;
};

export const ToolsNotAllowed = ["escalate", "skip", "support"];

export const stepsToDisplay: StepKey[] = [
  "topic",
  "tools",
  "context",
  "rerank",
  "strict",
  "plan",
  "detect-language",
  "prereqs",
  "images",
  "tool-invocation",
  "tool-response",
  "thinking",
  "additional-context",
];

function isChainOfThoughtStep(
  step: ChainOfThoughtStep | AiResponseThoughtStep
): step is ChainOfThoughtStep {
  return (
    "stepKey" in step && typeof step.stepKey === "string" && !("value" in step)
  );
}

export function adaptSteps(
  steps: (ChainOfThoughtStep | AiResponseThoughtStep)[]
): ChainOfThoughtStep[] {
  return steps.flatMap((step): ChainOfThoughtStep[] => {
    if (isChainOfThoughtStep(step)) {
      return [step];
    }

    const aiStep = step;
    const stepValue = aiStep.value;

    switch (aiStep.stepKey) {
      case ThoughtStepKey.TOOLS: {
        if ("allowedTools" in stepValue) {
          return [
            {
              stepKey: "tools",
              allowedTools: stepValue.allowedTools,
              unavailable:
                "unavailable" in stepValue ? stepValue.unavailable : undefined,
            },
          ];
        }
        break;
      }

      case ThoughtStepKey.CONTEXT: {
        if ("allowedSources" in stepValue && "contextData" in stepValue) {
          return [
            {
              stepKey: "context",
              allowedSources: stepValue.allowedSources,
              userQuery: stepValue.userQuery || "",
              contextData: {
                knowledgeRawContext: stepValue.contextData.knowledgeRawContext,
                ticketsRawContext: stepValue.contextData.ticketsRawContext,
                wikiKnowledgeContext:
                  stepValue.contextData.wikiKnowledgeContext,
                perplexityResponse: stepValue.contextData.perplexityResponse,
              },
            },
          ];
        }
        break;
      }

      case ThoughtStepKey.RERANK: {
        if ("rerankResults" in stepValue) {
          return [
            {
              stepKey: "rerank",
              rerankResults: {
                context: stepValue.rerankResults?.context || [],
                sources: {
                  kb: stepValue.rerankResults?.sources.kb || [],
                  tickets: stepValue.rerankResults?.sources.tickets || [],
                  wiki: stepValue.rerankResults?.sources.wiki || [],
                },
                contextExport: stepValue.rerankResults?.contextExport || [],
                isKnowledgeExist:
                  stepValue.rerankResults?.isKnowledgeExist || false,
              },
            },
          ];
        }
        break;
      }

      case ThoughtStepKey.STRICT: {
        if ("isStrictMode" in stepValue && "isAllowed" in stepValue) {
          return [
            {
              stepKey: "strict",
              isStrictMode: stepValue.isStrictMode,
              isAllowed: stepValue.isAllowed,
            },
          ];
        }
        break;
      }

      case ThoughtStepKey.PLAN: {
        if ("planContent" in stepValue) {
          return [
            {
              stepKey: "plan",
              planContent: stepValue.planContent || "",
            },
          ];
        }
        break;
      }

      case ThoughtStepKey.DETECT_LANGUAGE: {
        if ("detectedLanguage" in stepValue) {
          return [
            {
              stepKey: "detect-language",
              detectedLanguage: stepValue.detectedLanguage,
            },
          ];
        }
        break;
      }

      case ThoughtStepKey.THINKING: {
        if ("isThinkingNeeded" in stepValue && "reason" in stepValue) {
          return [
            {
              stepKey: "thinking",
              isThinkingNeeded: stepValue.isThinkingNeeded,
              reason: stepValue.reason,
            },
          ];
        }
        break;
      }

      case ThoughtStepKey.PREREQS: {
        if ("prerequisites" in stepValue) {
          return [
            {
              stepKey: "prereqs",
              prerequisites: {
                isAllPrerequisitesMet:
                  stepValue.prerequisites.isAllPrerequisitesMet,
                prerequisitesProvided:
                  stepValue.prerequisites.prerequisitesProvided,
                prerequisitesNotProvided:
                  stepValue.prerequisites.prerequisitesNotProvided || [],
                reason: stepValue.prerequisites.reason,
              },
            },
          ];
        }
        break;
      }

      case ThoughtStepKey.IMAGES: {
        if ("processedImages" in stepValue) {
          return [
            {
              stepKey: "images",
              processedImages: {
                images: stepValue.processedImages.images,
              },
            },
          ];
        }
        break;
      }

      case ThoughtStepKey.ANALYSIS: {
        if ("plan" in stepValue) {
          return [
            {
              stepKey: "plan",
              planContent: stepValue.plan,
            },
          ];
        }
        break;
      }

      case ThoughtStepKey.ADDITIONAL_CONTEXT: {
        if ("additionalContext" in stepValue) {
          return [
            {
              stepKey: "additional-context",
              additionalContext: stepValue.additionalContext ?? {},
            },
          ];
        }
        break;
      }

      case ThoughtStepKey.TOPIC: {
        if ("topic" in stepValue) {
          return [
            {
              stepKey: "topic",
              topic: String(stepValue.topic),
            },
          ];
        }
        break;
      }

      default:
        break;
    }

    return [];
  });
}
